import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PlanGestionMobilite } from '../../../_models/PlanGestionMobilite';
import MapView from '@arcgis/core/views/MapView';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import Query from '@arcgis/core/rest/support/Query';
import Graphic from '@arcgis/core/Graphic';
import { ConfigService } from '../../../_services/local/config.service';
import { GridWrapper } from '../../../_models/GridWrapper';
import { AssociationSegment } from '../../../_models/AssociationSegment';
import { of } from 'rxjs';
import { GridComponent } from '@progress/kendo-angular-grid';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureSet from '@arcgis/core/rest/support/FeatureSet';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';

@Component({
  selector: 'app-pgm-dialog-edit',
  templateUrl: './pgm-dialog-edit.component.html',
  styleUrls: ['./pgm-dialog-edit.component.scss'],
})
export class PgmDialogEditComponent implements OnInit, AfterViewInit {
  @ViewChild('kendoGrid', { static: true }) private kendoGrid!: GridComponent;
  @ViewChild('mapViewNode', { static: true }) mapViewNode!: ElementRef;
  height = document.body.clientHeight * 0.8 - (48 + 48 + 150 + 50);
  gridWrapper!: GridWrapper<AssociationSegment>;

  private mapView!: MapView;
  private selectedSegmentGraphicsLayer = new GraphicsLayer({
    id: 'selected-segment-layer',
  });

  constructor(
    private configService: ConfigService,
    private dialogRef: MatDialogRef<PgmDialogEditComponent>,
    @Inject(MAT_DIALOG_DATA) public pgm: PlanGestionMobilite
  ) {}

  ngOnInit(): void {
    this.gridWrapper = new GridWrapper<AssociationSegment>(
      'AssociationSegment',
      of(this.pgm.associations),
      {
        logic: 'or',
        filters: [
          {
            field: 'nomSegment',
            operator: 'contains',
          },
          {
            field: 'descriptionTypeEs',
            operator: 'contains',
          },
        ],
      },
      this.kendoGrid
    );
  }

  ngAfterViewInit(): void {
    this.initializeMap().then(async () => {
      await this.loadSegmentForAssociation();
      this.setTableRowsForHighlight();
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private async initializeMap(): Promise<void> {
    this.mapView = await EsriHelpers.GenerateMapAsync(
      this.mapViewNode.nativeElement,
      this.configService.Config.mapPgmVisualisationId,
      [this.selectedSegmentGraphicsLayer],
      { activateIdentify: false }
    );

    this.mapView.map.layers.forEach(async (layer) => {
      if (layer instanceof MapImageLayer && layer.sublayers?.length > 0) {
        await Promise.all(layer.sublayers.map((x) => x.load()));
        const promises: Promise<FeatureSet>[] = [];

        layer.sublayers.forEach((sl) => {
          const field = this.configService.Config.mapPgmVisualisationFilters[sl.id];
          sl.definitionExpression = `${field} = ${this.pgm.idHisto}`;

          const query = sl.createQuery();
          query.where = `${field} = ${this.pgm.idHisto}`;
          query.returnGeometry = true;

          promises.push(sl.queryFeatures(query));
        });

        const featureSets = await Promise.all(promises);
        const graphics = featureSets.map((f) => f.features).reduce((prev, cur) => prev.concat(cur));
        const geometries = graphics.filter((g) => g.geometry).map((g) => g.geometry);
        await this.mapView.goTo(geometryEngine.buffer(geometries, 50, 'meters'));
      }
    });
  }

  private setTableRowsForHighlight(): void {
    setTimeout(() => {
      const rows = this.kendoGrid.wrapper.nativeElement.querySelectorAll('tbody tr') as HTMLTableRowElement[];
      for (const row of rows) {
        // @ts-ignore
        if (!row.eventListeners('mouseover').length) {
          row.addEventListener('mouseover', this.onOver.bind(this, row), {
            passive: true,
          });
          row.addEventListener('mouseleave', () => this.mapView.graphics.removeAll(), { passive: true });
        }
      }
    }, 100);
  }

  public onOver(row: HTMLTableRowElement): void {
    console.debug('over');
    const rowIndex = Number(row.getAttribute('data-kendo-grid-item-index'));
    const assos: AssociationSegment = this.gridWrapper.gridData.data[rowIndex] as AssociationSegment;

    for (const graphic of this.selectedSegmentGraphicsLayer.graphics) {
      if (
        this.configService.Config.layers.segment.idSegmentField &&
        graphic.attributes[this.configService.Config.layers.segment.idSegmentField] == assos.idSegment
      ) {
        EsriHelpers.highlight(this.mapView, [graphic]);
      }
    }
  }

  private async loadSegmentForAssociation(): Promise<void> {
    if (this.pgm.associations.length === 0) {
      return;
    }

    const query = new Query();
    query.where = `idSegment in (${this.pgm.associations.map((x) => x.idSegment).join(',')})`;
    query.outFields = ['*'];
    query.returnGeometry = true;
    const fs = await EsriHelpers.query(this.configService.Config.segmentUrl, query);
    for (const graphic of fs.features) {
      this.selectedSegmentGraphicsLayer.graphics.add(
        new Graphic({
          geometry: graphic.geometry,
          symbol: undefined,
          attributes: graphic.attributes,
        })
      );
    }
  }
}
